import { useEffect, useRef } from 'react';

/* ══════════════════════════════════════════════════════════════════════════
   MÁTRIX-HÁTTÉR A BELÉPŐ OLDALHOZ — lefelé hulló karakteroszlopok, canvasben
   ─────────────────────────────────────────────────────────────────────────
   - Csak a terminál zöldjei, alacsony átlátszósággal (fej 0.2, a csík vége
     felé ~0.03), hogy a kártya szövege tökéletesen olvasható maradjon.
   - Karakterek: számok és kripto-jellegű jelek, tickerbetűk — nem katakana.
   - ~22 fps-re fojtott requestAnimationFrame; rejtett fülön nem rajzol.
   - Mobilon szélesebb oszlopköz, tehát kevesebb oszlop.
   - prefers-reduced-motion esetén egyetlen statikus kép, animáció nélkül.
   - Külső könyvtár nélkül.
   ══════════════════════════════════════════════════════════════════════════ */

const GLYPHS = '0123456789₿Ξ$%▲▼BTCETHSOLXRPADAUSDT+-.'.split('');
const HEAD = '0,255,144';   // #00ff90
const TAIL = '0,204,112';   // #00cc70
const HEAD_ALPHA = 0.2;
const TRAIL = 14;           // karakter / csík
const FRAME_MS = 1000 / 22;

export default function MatrixRain() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let cell = 16;
    let cols = 0;
    let rows = 0;
    let drops = [];
    let raf = 0;
    let last = 0;
    let tick = 0;

    const glyph = (c, r) => GLYPHS[(c * 31 + r * 17 + (tick >> 3) * 7) % GLYPHS.length];

    function setup() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      const mobile = w < 640;
      cell = mobile ? 15 : 16;
      const gap = mobile ? 2.4 : 1.35;      // oszlopköz a cellaszélesség arányában
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.font = `${cell - 2}px 'SF Mono', 'Fira Code', Consolas, monospace`;
      ctx.textBaseline = 'top';
      cols = Math.floor(w / (cell * gap));
      rows = Math.ceil(h / cell);
      drops = Array.from({ length: cols }, (_, i) => ({
        x: Math.round(i * cell * gap + cell * 0.3),
        y: -Math.random() * rows,
        speed: 0.35 + Math.random() * 0.55,
      }));
    }

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < drops.length; i++) {
        const d = drops[i];
        const headRow = Math.floor(d.y);
        for (let k = 0; k < TRAIL; k++) {
          const row = headRow - k;
          if (row < 0 || row > rows) continue;
          const a = k === 0 ? HEAD_ALPHA : HEAD_ALPHA * 0.8 * (1 - k / TRAIL) + 0.015;
          ctx.fillStyle = `rgba(${k === 0 ? HEAD : TAIL},${a.toFixed(3)})`;
          ctx.fillText(glyph(i, row), d.x, row * cell);
        }
      }
    }

    function step() {
      for (const d of drops) {
        d.y += d.speed;
        if (d.y - TRAIL > rows) {
          d.y = -Math.random() * rows * 0.5;
          d.speed = 0.35 + Math.random() * 0.55;
        }
      }
      tick++;
    }

    function loop(t) {
      raf = requestAnimationFrame(loop);
      if (t - last < FRAME_MS) return;
      last = t;
      step();
      draw();
    }

    function start() {
      if (reduced || raf || document.hidden) return;
      last = 0;
      raf = requestAnimationFrame(loop);
    }
    function stop() {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    }

    setup();
    if (reduced) {
      // Statikus kép: a csíkok véletlen helyen, mozgás nélkül.
      drops.forEach(d => { d.y = Math.random() * (rows + TRAIL); });
      draw();
    } else {
      start();
    }

    const onVisibility = () => (document.hidden ? stop() : start());
    let resizeTimer = 0;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setup();
        if (reduced) {
          drops.forEach(d => { d.y = Math.random() * (rows + TRAIL); });
          draw();
        }
      }, 150);
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('resize', onResize);
    return () => {
      stop();
      clearTimeout(resizeTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return <canvas ref={ref} className="eg-matrix" aria-hidden="true" />;
}
